// src/layout/DashboardLayoutAccount.js
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Typography,
  ListItemIcon,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FilterListIcon from "@mui/icons-material/FilterList";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { ThemeSwitcher } from "@toolpad/core";
import { logout } from "../redux/slices/authSlice";
import { logoutUser } from "../api/authApi";
import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { DemoProvider } from "@toolpad/core/internal";
import { createTheme } from "@mui/material/styles";
import { SwitchTransition, CSSTransition } from "react-transition-group";

import "../layout/routeTransitions.css";
import logo from "../assets/smiledent-logo.webp";

// Lazy‐loaded pages
const ListPage        = React.lazy(() => import("../pages/ListPage"));
const FormPage        = React.lazy(() => import("../pages/FormPage"));
const PermissionsPage = React.lazy(() => import("../pages/rbac/PermissionsPage"));

const NAV_ITEMS = [
  { segment: "dashboard",        title: "Dashboard",    icon: <DashboardIcon /> },
  { segment: "form",             title: "Send File",    icon: <FilterListIcon /> },
  { segment: "rbac/permissions", title: "Role Manager", icon: <SettingsIcon /> },
];

const demoTheme = createTheme({
  cssVariables: { colorSchemeSelector: "data-toolpad-color-scheme" },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: "#e6401f",
          dark: "#b72d12",
          light: "#f26a3d",
          contrastText: "#ffffff",
        },
        secondary: {
          main: "#1565c0",
          dark: "#0d47a1",
          light: "#5e92f3",
          contrastText: "#ffffff",
        },
        background: {
          default: "#faf8f6",
          paper: "#ffffff",
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: "#f26a3d",
          dark: "#e6401f",
          light: "#ff9a73",
          contrastText: "#2b0d05",
        },
        secondary: {
          main: "#5e92f3",
          dark: "#1565c0",
          light: "#90caf9",
          contrastText: "#061527",
        },
        background: {
          default: "#1f1714",
          paper: "#2a201c",
        },
      },
    },
  },
  breakpoints:   { xs: 0, sm: 600, md: 600, lg: 1200, xl: 1536 },
  shape: { borderRadius: 8 },
});

export default function DashboardLayoutAccount({ window }) {
  const user     = useSelector((s) => s.auth.user);
  const role     = user?.roleName;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Logout logic
  const handleLogout = React.useCallback(async () => {
    try {
      await logoutUser();
    } catch (err) {
      console.error("Logout request failed:", err);
    } finally {
      dispatch(logout());
      navigate("/signin");
    }
  }, [dispatch, navigate]);

  // Toolpad session/auth
  const session = React.useMemo(
    () => ({ user: user ? { name: user.name } : null }),
    [user]
  );
  const authentication = React.useMemo(
    () => ({ signIn: () => {}, signOut: handleLogout }),
    [handleLogout]
  );

  // Sidebar navigation with icon hover styling
  const navigation = React.useMemo(() => {
    return NAV_ITEMS
      .filter((it) => !(role === "User" && it.segment === "rbac/permissions"))
      .map((item) => {
        const styledIcon = React.cloneElement(item.icon, {
          sx: {
            color: 'inherit',
            transition: 'color 0.2s',
            '&:hover': {
              color: (theme) => theme.palette.primary.main,
            },
          },
        });
        return {
          ...item,
          icon: styledIcon,
          onClick: () => navigate(`/${item.segment}`),
          selected: location.pathname === `/${item.segment}`,
        };
      });
  }, [role, navigate, location.pathname]);

  // Compute initials
  const initials = React.useMemo(() => {
    if (!user?.name) return "";
    return user.name
      .split(" ")
      .map((p) => p[0])
      .join("")
      .toUpperCase();
  }, [user]);

  // Popover state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openPopover = Boolean(anchorEl);
  const handleAvatarClick = (e) => setAnchorEl(e.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  const handleSignOut = () => {
    handlePopoverClose();
    handleLogout();
  };

  const demoWindow = window ? window() : undefined;

  // Custom toolbar actions
  const ToolbarActions = React.useCallback(
    () => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <ThemeSwitcher />
        <IconButton size="small" onClick={handleAvatarClick}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
            {initials}
          </Avatar>
        </IconButton>
      </Box>
    ),
    [initials]
  );

  return (
    <DemoProvider window={demoWindow}>
      <AppProvider
        session={session}
        authentication={authentication}
        navigation={navigation}
        branding={{
          logo: <img src={logo} alt="SmileDent Dental Studio" style={{ maxHeight: 40 }} />,
          title: "",
          homeUrl: "/dashboard",
        }}
        theme={demoTheme}
        window={demoWindow}
      >
        <DashboardLayout
          defaultSidebarCollapsed
          slots={{ toolbarActions: ToolbarActions }}
        >
          {/* Custom Popover */}
          <Menu
            anchorEl={anchorEl}
            open={openPopover}
            onClose={handlePopoverClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
                {initials}
              </Avatar>
              <Typography variant="body1">{user?.name}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>

          {/* Main Content */}
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={location.pathname}
              classNames="fade"
              timeout={200}
            >
              <div style={{ height: "100%" }}>
                <React.Suspense fallback={null}>
                  <Routes location={location}>
                    <Route path="/dashboard"        element={<ListPage />} />
                    <Route path="/form"             element={<FormPage />} />
                    <Route path="/rbac/permissions" element={<PermissionsPage />} />
                    <Route path="*"                 element={<ListPage />} />
                  </Routes>
                </React.Suspense>
              </div>
            </CSSTransition>
          </SwitchTransition>
        </DashboardLayout>
      </AppProvider>
    </DemoProvider>
  );
}

DashboardLayoutAccount.propTypes = {
  window: PropTypes.func,
};
